import { Fragment, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { en, de } from 'yup-locales';
import * as Sentry from "@sentry/react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { api } from "helpers/axios";
import { empty, errorMessage, getTextFromHtml, isTrue, scrollToTop } from "helpers/common";
import { toast } from "helpers/toast";
import { getLang, getModule, getPermissions } from "store/userSlice";
import { getAllCustomNames, fetchFields, getFixedFields, getFreeFields, getConditionalFields, getCalculationRelatedFields } from "store/fieldsSlice";
import { fetchBalanceScorecardList, getBalanceScorecardList } from "store/balanceScorecardSlice";
import { fetchBudgetSettings, getBudgetSettings } from "store/budgetSettingsSlice";
import { fetchGroups, getGroups } from "store/groupsSlice";
import { getGeneralSettings } from "store/generalSettingsSlice";
import { fetchGlobalPayfactorValues, selectGlobalPayfactorValues } from "store/payfactorSlice";
import { useTranslation } from "translation/useTranslation";
import ErrorAlert from "components/ui/ErrorAlert";
import Link from "components/ui/Link";
import PayGradeSelect from "components/EmployeeManagement/inputs/PayGradeSelect";
import DepartmentSelect from "components/EmployeeManagement/inputs/DepartmentSelect";
import { fetchStateEmployeeFields, getStateEmployeeSections } from "store/stateEmployeeSlice";
import EmployeeSelector from "./EmployeeSelector";
import EmployeeInstanceEditor from "./EmployeeInstanceEditor";
import ActionChangePreview from "./ActionChangePreview";
import CreateBalancedScorecardAction from "./CreateBalancedScorecardAction";
import ChangeFirstScorecardAction from "./ChangeFirstScorecardAction";
import useEmployeePool from "./useEmployeePool";
import { canRunAction } from "./actionPermissions";
import { applySelectionChange, buildWhoList, pairInstanceValues } from "./actionChangePreviewHelpers";
import { StateOverwriteForm, resolveStateFillFields, buildStateDataToUpdate } from "./DB-FillFields";
import { findEmptyRequiredStateField } from "./DB-fillFieldsHelpers";
import { isDbModule } from "helpers/module";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import EmployeeDeactivationModal from "components/EmployeeManagement/EmployeeDeactivationModal";
import { finalAssignmentConfirmationDetails, withFinalAssignmentConfirmation } from "helpers/employeeDeactivation";
import { excludeAssignmentOwnedEmployeeFields } from "helpers/employeeAssignmentFields";
import { Button, Field, Input, InputAffix, DateInput, NativeSelect, Select } from "components/ui";
import { employeeManagementPath } from "helpers/employeeDeeplink";

// Rendered inside the parent field's cell and sent alongside the parent when it's checked.
const COMPANION_FIELD_KEYS = ['pay_grade_level', 'uses_individual_pay_factor', 'manual_average_value'];
const CURRENCY_FIELD_KEYS = ['annual_compensation', 'individual_ceiling'];
const DAYS_SUFFIX_FIELD_KEYS = ['missing_days', 'working_days_per_week'];
const ZERO_WHEN_EMPTY_FIELD_KEYS = ['missing_months', 'missing_days'];
// Fields deliberately not offered in the fill form.
const EXCLUDED_ACTION_FIELD_KEYS = ['trend', 'balanced_scorecard_template', 'balanced_scorecard_template_group', 'budget', 'group', 'reference_balanced_scorecard_setting_name', 'reference_budget_name', 'reference_group_name'];
// Payroll fields; every other non-free field goes to "Personaldaten".
const ABRECHNUNG_FIELD_KEYS = ['working_hours', 'exclude_from_payout', 'pay_grade', 'individual_pay_factor', 'annual_compensation', 'individual_ceiling', 'missing_months', 'missing_days', 'working_days_per_week'];
// Sent as `employee_information.<slug>` (name-keyed).
const EMPLOYEE_INFO_FIELD_TYPES = ['free', 'conditional'];
const IDENTITY_FIELD_KEYS = ['first_name', 'last_name', 'email', 'employee_number'];
const EMPLOYEE_DATA_DISPLAY = 'employee_data';
const STATE_EXTRA_FIELD_KEYS = ['employment_entry_date', 'employment_exit_date', 'employee_group'];
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);

// Overwrite mode: one data_to_update list for every selected employee, from the form values.
export const buildBulkDataToUpdate = (data) => {
  const checkedKeys = (data.fields || []).filter(f => f);
  const data_to_update = [];

  for (const key of checkedKeys) {
    if (key === 'balanced_scorecard_template_group') continue;

    let value = key.includes('employee_information.')
      ? data['employee_information'][key.split('.')[1]]
      : data[key];

    if (['employment_entry_date', 'employment_exit_date'].includes(key)) {
      // Ticked + empty clears the date.
      value = empty(value) ? '' : dayjs(value, 'DD.MM.YYYY').format('YYYY-MM-DD');
    } else if (key === 'exclude_from_payout') {
      value = data['exclude_from_payout'] === 'true';
      data_to_update.push({ key: 'exclude_from_payout_reason', value: data['exclude_from_payout_reason'] ?? null });
    } else if (key === 'pay_grade') {
      data_to_update.push({ key: 'pay_grade_level', value: data['pay_grade_level'] ?? null });
    } else if (key === 'individual_pay_factor') {
      data_to_update.push({ key: 'uses_individual_pay_factor', value: !!data['uses_individual_pay_factor'] });
    } else if (key === 'trend' && value === 'yes_manual_value') {
      data_to_update.push({ key: 'manual_average_value', value: data['manual_average_value'] ?? null });
    }

    // The employee_group field is written as the employee_group_id column.
    // value ?? '' keeps the key in the JSON body; an untouched select is undefined.
    data_to_update.push({ key: key === 'employee_group' ? 'employee_group_id' : key, value: value ?? '' });
  }

  return data_to_update;
};

// Per-employee mode: data_to_update for one employee from its values and ticked fields.
// Returns { entries, error }; error blocks the pre-check.
export const buildInstanceDataToUpdate = (vals, checked, translate) => {
  const checkedKeys = Object.keys(checked).filter(k => checked[k]);
  const entries = [];

  for (const key of checkedKeys) {
    let value = vals[key];

    if (['employment_entry_date', 'employment_exit_date'].includes(key)) {
      if (value === '' || value === null || value === undefined) {
        value = ''; // ticked + empty clears the date
      } else if (!dayjs(value, 'DD.MM.YYYY').isValid()) {
        return { entries: [], error: translate('form.valid_format_date', 'Ungültiges Datumsformat (TT.MM.JJJJ).') };
      } else {
        value = dayjs(value, 'DD.MM.YYYY').format('YYYY-MM-DD');
      }
    } else if (key === 'exclude_from_payout') {
      value = vals['exclude_from_payout'] === 'true';
      if (value && empty(vals['exclude_from_payout_reason'])) {
        return { entries: [], error: translate('field.error.exclude_from_payout_reason', 'Bitte geben Sie einen Ausschlussgrund an') };
      }
      entries.push({ key: 'exclude_from_payout_reason', value: vals['exclude_from_payout_reason'] ?? '' });
    } else if (key === 'pay_grade') {
      entries.push({ key: 'pay_grade_level', value: vals['pay_grade_level'] ?? null });
    } else if (key === 'individual_pay_factor') {
      if (vals['uses_individual_pay_factor'] && empty(value)) {
        return { entries: [], error: `${translate('field.individual_pay_factor')}: ${translate('form.field_required', 'Dieses Feld ist erforderlich')}` };
      }
      entries.push({ key: 'uses_individual_pay_factor', value: !!vals['uses_individual_pay_factor'] });
    } else if (key === 'trend' && vals['trend'] === 'yes_manual_value') {
      entries.push({ key: 'manual_average_value', value: vals['manual_average_value'] ?? null });
    } else if (key === 'employee_group' && empty(value)) {
      // The backend resolves the group by hash id; an empty one cannot be cleared.
      return { entries: [], error: `${translate('field.employee_group')}: ${translate('form.field_required', 'Dieses Feld ist erforderlich')}` };
    }

    entries.push({ key: key === 'employee_group' ? 'employee_group_id' : key, value: value ?? '' });
  }

  return { entries, error: null };
};

export default function FillFieldsAction({ filters, page, setPage, filterCount, setFilterCount, onBack, allowedFieldKeys, restrictToUpdateData = false, backTo = null }) {
  const permissions = useSelector(getPermissions);
  // Scorecard tabs only for users who may create scorecards (LIX-1777); the API refuses the rest.
  const canCreateScorecards = canRunAction('create_balanced_scorecard', permissions);
  const canChangeFirstScorecard = canRunAction('change_first_scorecard', permissions);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const lang = useSelector(getLang);

  const { translate, editableTranslate, editableTranslateString } = useTranslation();

  const currentModule = useSelector(getModule);
  const isDbModuleActive = useMemo(() => isDbModule(currentModule), [currentModule]);

  const fieldCustomNames = useSelector(getAllCustomNames);
  const fixedFields = useSelector(getFixedFields);
  const freeFieldsRaw = useSelector(getFreeFields);
  const conditionalFields = useSelector(getConditionalFields);
  const calculationRelatedFields = useSelector(getCalculationRelatedFields);
  const stateSections = useSelector(getStateEmployeeSections);
  const balancedScorecardList = useSelector(getBalanceScorecardList);
  const budgets = useSelector(getBudgetSettings);
  const groups = useSelector(getGroups);
  const settings = useSelector(getGeneralSettings);

  const activePayfactorValues = useSelector(selectGlobalPayfactorValues);

  const [actionType, setActionType] = useState('update_data');
  const [actionMode, setActionMode] = useState('overwrite'); // 'overwrite' | 'per_instance'
  const [poolPage, setPoolPage] = useState(1);
  const [poolPerPage, setPoolPerPage] = useState(20);
  const [searchInput, setSearchInput] = useState(''); // raw input
  const [poolSearch, setPoolSearch] = useState(''); // debounced term sent to the server

  // Switching overwrite <-> per-instance starts the employee list fresh (page 1, no search).
  const changeActionMode = (mode) => {
    if (mode === actionMode) return;
    setActionMode(mode);
    setSearchInput('');
    setPoolSearch('');
    setPoolPage(1);
  };
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState('danger');
  const [selected, setSelected] = useState([]); // overwrite: employee hash_ids
  const [selectedEmployees, setSelectedEmployees] = useState({}); // overwrite: hash_id -> display data (step-2 who list)
  const [checkedEmployees, setCheckedEmployees] = useState({}); // per-instance: hash_id -> display data (step-2 who list)
  const [instanceChecked, setInstanceChecked] = useState([]); // per-instance: checked hash_ids
  const [instanceData, setInstanceData] = useState({}); // per-instance: { [hash_id]: { [fieldKey]: value } }
  const [instanceFieldChecked, setInstanceFieldChecked] = useState({}); // per-instance: { [hash_id]: { [fieldKey]: true } }
  // State-employee module: fields come from the state form definition (not fieldsSlice).
  const [stateChecked, setStateChecked] = useState({}); // overwrite: { [identifier]: true }
  const [stateValues, setStateValues] = useState({}); // overwrite: { [identifier]: value }
  const [stateInstanceChecked, setStateInstanceChecked] = useState({}); // per-instance: { [hash]: { [identifier]: true } }
  const [stateInstanceValues, setStateInstanceValues] = useState({}); // per-instance: { [hash]: { [identifier]: value } }
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [finalAssignmentCount, setFinalAssignmentCount] = useState(null);

  const softwareSettings = useMemo(() => {
    const obj = {};
    if (settings) settings.forEach(s => { obj[s.key] = s.value; });
    return obj;
  }, [settings]);

  useEffect(() => {
    if (!fixedFields) dispatch(fetchFields());
    if (!balancedScorecardList) dispatch(fetchBalanceScorecardList());
    if (!budgets) dispatch(fetchBudgetSettings());
    if (!groups) dispatch(fetchGroups());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchGlobalPayfactorValues());
  }, [dispatch]);

  useEffect(() => {
    if (isDbModuleActive && !stateSections) dispatch(fetchStateEmployeeFields());
  }, [dispatch, isDbModuleActive, stateSections]);

  // Key is `employee_information.<slug>` for free/conditional fields, plain slug for real columns.
  const editableFields = useMemo(() => {
    const resolve = (fields) => (fields || [])
      .filter(f => f.is_active && (f.display_in || []).includes(EMPLOYEE_DATA_DISPLAY))
      .map(f => ({
        ...f,
        key: EMPLOYEE_INFO_FIELD_TYPES.includes(f.field_type) ? `employee_information.${f.slug}` : f.slug,
        label: f.name,
      }))
      .filter(f => !IDENTITY_FIELD_KEYS.includes(f.slug));

    const personal = excludeAssignmentOwnedEmployeeFields(resolve(fixedFields));
    const abrechnung = [
      ...resolve(calculationRelatedFields).filter(f => f.slug !== 'pay_grade_level'),
      ...resolve(conditionalFields).filter(f => f.source !== 'state_employee_config'),
    ];
    const free = resolve(freeFieldsRaw);
    const all = [...personal, ...abrechnung, ...free];

    // Validation-driven actions restrict the form to the fields the validation
    // asks for. Match against both the resolved key and the plain slug, since
    // free/conditional fields are keyed as `employee_information.<slug>`.
    if (!allowedFieldKeys || allowedFieldKeys.length === 0) return all;
    const allowed = new Set(allowedFieldKeys);
    return all.filter(f => allowed.has(f.key) || allowed.has(f.slug));
  }, [fixedFields, calculationRelatedFields, conditionalFields, freeFieldsRaw, allowedFieldKeys]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPoolSearch(searchInput.trim());
      setPoolPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPoolPage(1);
  }, [filters]);

  const { employees, pagination, loading: loadingPool, loaded: poolLoaded, error: poolError } = useEmployeePool({
    filters,
    page: poolPage,
    perPage: poolPerPage,
    search: poolSearch,
    onTotal: setFilterCount,
  });

  useEffect(() => {
    if (poolError) setError(poolError);
  }, [poolError]);

  const handlePoolPerPageChange = (n) => {
    setPoolPerPage(n);
    setPoolPage(1);
  };

  const balancedScorecardListFiltered = useMemo(() => {
    if (balancedScorecardList) {
      return balancedScorecardList.filter(bs => isDbModuleActive ? bs?.form_definition?.type === 'custom_balanced_scorecard_setting' : bs?.form_definition?.type !== 'custom_balanced_scorecard_setting');
    }
    return balancedScorecardList;
  }, [balancedScorecardList, isDbModuleActive]);

  const [personaldatenFields, abrechnungsdatenFields, freeFields] = useMemo(() => {
    const personal = [];
    const abrechnung = [];
    const free = [];

    for (const f of editableFields) {
      if (COMPANION_FIELD_KEYS.includes(f?.key)) continue;
      if (EXCLUDED_ACTION_FIELD_KEYS.includes(f?.key)) continue;
      if (f?.field_type === 'free') {
        free.push(f);
      } else if (ABRECHNUNG_FIELD_KEYS.includes(f?.key) || f?.field_type === 'conditional' || f?.field_type === 'calculation_related') {
        abrechnung.push(f);
      } else {
        if (['employee_group_id', 'employee_group'].includes(f?.key) && !isDbModuleActive) continue;
        personal.push(f);
      }
    }

    return [personal, abrechnung, free];
  }, [editableFields, isDbModuleActive]);

  // Accepts comma or dot as the decimal separator; empty is treated as null.
  const decimalField = (decimals, min, max, label) => yup.number().nullable()
    .transform((value, originalValue) => {
      if (originalValue === '' || originalValue === null || originalValue === undefined) return null;
      return parseFloat(String(originalValue).replace(',', '.'));
    })
    .typeError(translate(`form.valid_format_decimal_${decimals}`))
    .test('is-decimal', translate(`form.valid_format_decimal_${decimals}`), function (value) {
      if (value === null || value === undefined) return true;
      return new RegExp(`^\\d+(\\.\\d{1,${decimals}})?$`).test(String(value));
    })
    .min(min).max(max).label(label);

  const schema = yup.object({
    employment_entry_date: yup.string().notRequired().label(fieldCustomNames?.['field.employment_entry_date'] || translate('field.employment_entry_date'))
      .test('valid-date', 'Invalid Date', val => {
        if (!val) return true;
        return dayjs(val, 'DD.MM.YYYY').isValid();
      }),
    employment_exit_date: yup.string().notRequired().label(fieldCustomNames?.['field.employment_exit_date'] || translate('field.employment_exit_date'))
      .test('valid-date', 'Invalid Date', val => {
        if (!val) return true;
        return dayjs(val, 'DD.MM.YYYY').isValid();
      }),
    working_hours: softwareSettings && softwareSettings.working_hours_type === 'hours'
      ? yup.number().nullable().transform((value, originalValue) => originalValue === '' ? null : value).integer().min(0).max(50).label(fieldCustomNames?.['field.working_hours'] || translate('field.working_hours'))
      : yup.number().nullable().transform((value, originalValue) => {
        if (originalValue === '') return null;
        const normalized = originalValue.replace(',', '.');
        return parseFloat(normalized);
      }).test('is-decimal', translate('form.valid_format_decimal_2'), function (value) {
        if (value === null || value === undefined) return true;
        return /^\d+(\.\d{1,2})?$/.test(String(value));
      }).min(5).max(100).label(fieldCustomNames?.['field.working_hours'] || translate('field.working_hours')),
    exclude_from_payout_reason: yup.string().notRequired().when('exclude_from_payout', {
      is: (val) => (val === true || val === 'true'),
      then: (schema) => schema.required(translate('field.error.exclude_from_payout_reason')).min(10).max(50).label(translate('field.exclude_from_payout_reason'))
    }),
    balanced_scorecard_template_group: yup.string().required().label(fieldCustomNames?.['field.balanced_scorecard_template'] || translate('state_employee.rating_mode')),
    balanced_scorecard_template: yup.string().required().label(translate(isDbModuleActive ? 'state_employee.rating_mode.template' : 'field.balanced_scorecard_template')),
    budget: yup.string().required().label(fieldCustomNames?.['field.budget'] || translate('field.budget')),
    group: yup.string().required().label(translate(fieldCustomNames?.['field.group'] || translate('field.group'))),
    employee_group: yup.string().required().label(fieldCustomNames?.['field.employee_group'] || translate('field.employee_group')),
    pay_grade: yup.string().nullable().max(10),
    pay_grade_level: yup.string().nullable().max(10),
    department: yup.string().nullable().max(255).label(fieldCustomNames?.['field.department'] || translate('field.department')),
    uses_individual_pay_factor: yup.boolean().nullable(),
    individual_pay_factor: decimalField(3, 0.001, 999999.999, fieldCustomNames?.['field.individual_pay_factor'] || translate('field.individual_pay_factor')).when('uses_individual_pay_factor', {
      is: true,
      then: (s) => s.required(),
      otherwise: (s) => s.notRequired(),
    }),
    annual_compensation: decimalField(2, 0, 999999.99, fieldCustomNames?.['field.annual_compensation'] || translate('field.annual_compensation')),
    individual_ceiling: decimalField(2, 0, 999999.99, fieldCustomNames?.['field.individual_ceiling'] || translate('field.individual_ceiling')),
    missing_months: decimalField(3, 0, 36, fieldCustomNames?.['field.missing_months'] || translate('field.missing_months')),
    missing_days: decimalField(3, 0, 366, fieldCustomNames?.['field.missing_days'] || translate('field.missing_days'))
      .integer(translate('form.valid_format_decimal_0')),
    working_days_per_week: decimalField(3, 1, 7, fieldCustomNames?.['field.working_days_per_week'] || translate('field.working_days_per_week'))
      .integer(translate('form.valid_format_decimal_0')),
    trend: yup.string().nullable(),
    manual_average_value: decimalField(2, 0, 999.99, translate('field.trend.yes_manual_value.value', 'Wert')).when('trend', {
      is: 'yes_manual_value',
      then: (s) => s.required(),
      otherwise: (s) => s.notRequired(),
    }),
    fields: yup.array().of(yup.string()).notRequired()
  });

  const { register, trigger, clearErrors, getValues, setValue, watch, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { fields: [], uses_individual_pay_factor: false, exclude_from_payout: 'false' }
  });

  const watchFields = watch('fields') || [];
  const watchExcludeFromPayout = watch('exclude_from_payout');
  const watchTemplateGroup = watch('balanced_scorecard_template_group');
  const watchPayGrade = watch('pay_grade');
  const watchPayGradeLevel = watch('pay_grade_level');
  const watchDepartment = watch('department');
  const watchUsesIndividualPayFactor = watch('uses_individual_pay_factor');
  const watchEmployeeGroup = watch('employee_group');

  useEffect(() => {
    yup.setLocale(lang === 'en' ? en : de);
  }, [lang]);

  const balancedScorecardTemplateGroupOptions = useMemo(() => {
    if (!balancedScorecardList) return [];
    const groupsMap = new Map();
    balancedScorecardList.forEach((bs) => {
      const group = bs?.balanced_scorecard_setting_group;
      if (group?.hash_id && !groupsMap.has(group.hash_id)) {
        groupsMap.set(group.hash_id, { name: group.name, hash_id: group.hash_id });
      }
    });
    return Array.from(groupsMap.values());
  }, [balancedScorecardList]);

  useEffect(() => {
    setValue('balanced_scorecard_template', null);
  }, [watchTemplateGroup]);

  const balancedScorecardTemplateOptions = useMemo(() => {
    if (!balancedScorecardList || !watchTemplateGroup) return [];
    return balancedScorecardList
      .filter(bs => bs?.balanced_scorecard_setting_group?.hash_id === watchTemplateGroup)
      .map(bs => {
        let label = bs?.balanced_scorecard_setting_group?.name
          ? bs.slug.replace(new RegExp(`^${bs.balanced_scorecard_setting_group.name}\\s*-\\s*`, 'i'), '')
          : bs.slug;
        return {
          name: translate('state_employee.rating_mode.template_' + label?.toLowerCase(), label),
          hash_id: bs.hash_id
        };
      });
  }, [balancedScorecardList, watchTemplateGroup]);

  useEffect(() => {
    if ([false, 'false'].includes(watchExcludeFromPayout)) {
      setValue('exclude_from_payout_reason', null);
    }
  }, [watchExcludeFromPayout]);

  // ---- payload builders ----------------------------------------------------

  const stateCheckboxFields = useMemo(
    () => (isDbModuleActive ? resolveStateFillFields(stateSections).checkboxFields : []),
    [isDbModuleActive, stateSections]
  );

  const stateExtraFields = useMemo(
    () => (isDbModuleActive ? personaldatenFields.filter(f => STATE_EXTRA_FIELD_KEYS.includes(f.key)) : []),
    [isDbModuleActive, personaldatenFields]
  );

  const buildFormDataToUpdate = () => buildBulkDataToUpdate(getValues());

  const buildOverwriteData = () => {
    const data_to_update = isDbModuleActive
      ? [...buildFormDataToUpdate(), ...buildStateDataToUpdate(stateCheckboxFields, stateChecked, stateValues)]
      : buildFormDataToUpdate();

    return { filters: filters || [], data_to_update, employee_hash_ids: selected };
  };

  const buildInstanceFormEntries = (hash) => buildInstanceDataToUpdate(instanceData[hash] || {}, instanceFieldChecked[hash] || {}, translate);

  // Only ticked fields are sent; a ticked-but-empty field is sent empty to clear it.
  // Error for the first ticked required state field left empty, or null.
  const stateRequiredError = (checked, values) => {
    const cf = findEmptyRequiredStateField(stateCheckboxFields, checked, values);
    if (!cf) return null;
    const label = cf.field?.custom_name || translate(cf.field?.translation_key, cf.field?.name || cf.key);
    return `${label}: ${translate('form.field_required', 'Dieses Feld ist erforderlich')}`;
  };

  const buildInstanceUpdates = () => {
    const updates = [];

    for (const hash of instanceChecked) {
      const { entries, error: vErr } = buildInstanceFormEntries(hash);
      if (vErr) return { updates: [], error: vErr };

      const stateErr = isDbModuleActive ? stateRequiredError(stateInstanceChecked[hash] || {}, stateInstanceValues[hash] || {}) : null;
      if (stateErr) return { updates: [], error: stateErr };

      const data_to_update = isDbModuleActive
        ? [...entries, ...buildStateDataToUpdate(stateCheckboxFields, stateInstanceChecked[hash] || {}, stateInstanceValues[hash] || {})]
        : entries;

      if (data_to_update.length > 0) updates.push({ employee_hash_id: hash, data_to_update });
    }

    return { updates, error: null };
  };

  // ---- review summary (overwrite) -----------------------------------------

  const stateChanges = (checkedMap, valuesMap) => {
    const arr = [];

    stateCheckboxFields.forEach((cf) => {
      if (!checkedMap?.[cf.key]) return;

      const f = cf.field;
      const name = f?.custom_name || translate(f?.translation_key, f?.name || f?.identifier);

      const formatValue = (v) => {
        if (f?.field_type === 'boolean') return isTrue(v) ? translate('form.yes') : translate('form.no');
        if (empty(v)) return '';
        const opt = (f?.options || []).find(o => String(o?.value) === String(v));
        if (opt) return opt?.label_translation_key ? translate(opt.label_translation_key, String(v)) : (opt?.label ?? String(v));
        if (f?.field_type === 'text') return getTextFromHtml(v);
        return String(v);
      };

      const value = cf.inputs.map(inp => formatValue(valuesMap?.[inp.identifier])).filter(v => v !== '').join(' ');
      arr.push({ label: name, value });
    });

    return arr;
  };

  const formatStandardChange = (key, getVal) => {
    let value = getVal(key);
    if (ZERO_WHEN_EMPTY_FIELD_KEYS.includes(key) && empty(value)) value = '0';

    let altLabel = '';
    let translationKey = '';
    for (let j = 0; j < editableFields.length; j++) {
      if (editableFields[j].key === key) {
        altLabel = editableFields[j].label;
        translationKey = editableFields[j]?.translation_key || '';
        break;
      }
    }

    let name = fieldCustomNames?.[translationKey] || translate(`field.${key.replace('_hash_id', '')}`, altLabel);

    if (isDbModuleActive) {
      if (key === 'balanced_scorecard_template_group') {
        name = fieldCustomNames?.['field.balanced_scorecard_template'] || translate('state_employee.rating_mode');
      } else if (key === 'balanced_scorecard_template') {
        name = translate('state_employee.rating_mode.template');
      }
    }

    if (key === 'exclude_from_payout') {
      name = fieldCustomNames?.['field.exclude_from_payout'] || translate(`field.exclude_from_payout`, altLabel);
      value = [true, 'true'].includes(getVal('exclude_from_payout')) ? translate('form.yes') + ': ' + (getVal('exclude_from_payout_reason') ?? '') : translate('form.no');
    } else if (['balanced_scorecard_template_group', 'balanced_scorecard_template', 'budget', 'employee_group_id', 'employee_group', 'group'].includes(key)) {
      let list = [];
      if (key === 'balanced_scorecard_template') {
        list = isDbModuleActive ? balancedScorecardTemplateOptions : balancedScorecardListFiltered;
      } else if (key === 'balanced_scorecard_template_group') list = balancedScorecardTemplateGroupOptions;
      else if (key === 'budget') list = budgets;
      else if (['group', 'employee_group_id', 'employee_group'].includes(key)) list = groups;

      for (const item of list || []) {
        if (item.hash_id === value) {
          value = item.name;
          break;
        }
      }
    }

    return { label: name, value: value ?? '' };
  };

  const getChangedValues = () => {
    const data = getValues();
    const getVal = (k) => k.includes('employee_information.') ? data['employee_information']?.[k.split('.')[1]] : data[k];
    const checkedKeys = (data.fields || []).filter(f => f);
    const arr = checkedKeys.map(key => formatStandardChange(key, getVal));
    if (isDbModuleActive) arr.push(...stateChanges(stateChecked, stateValues));
    return arr;
  };

  const getInstanceChangedValues = (hash) => {
    const record = instanceData[hash] || {};
    const getVal = (k) => record[k];
    const checkedKeys = Object.keys(instanceFieldChecked[hash] || {}).filter(k => instanceFieldChecked[hash][k]);
    const arr = checkedKeys.map(key => formatStandardChange(key, getVal));
    if (isDbModuleActive) arr.push(...stateChanges(stateInstanceChecked[hash] || {}, stateInstanceValues[hash] || {}));
    return arr;
  };

  // ---- step transitions ----------------------------------------------------

  const fetchPreview = async () => {
    if (actionType === 'deactivate') return true;

    setLoadingPreview(true);
    setPreview(null);
    try {
      let res;
      if (actionMode === 'overwrite') {
        res = await api.post('/employees/bulk-update/preview', buildOverwriteData());
      } else {
        res = await api.post('/employees/batch-save', { updates: buildInstanceUpdates().updates, dry_run: true });
      }
      if (res.status === 200) {
        setPreview(res.data?.data || null);
        return true;
      }
      return false;
    } catch (err) {
      setErrorType('danger');
      setError(errorMessage(err, 'pre-check'));
      Sentry.captureException(err);
      return false;
    } finally {
      setLoadingPreview(false);
    }
  };

  const goToPrecheck = async () => {
    setError(null);
    setErrorType('danger');

    if (actionMode === 'overwrite') {
      if (actionType === 'deactivate') {
        if (selected.length < 1) {
          setError(errorMessage(translate('batch_update.checked_employees_required'), 'fill fields'));
          scrollToTop();
          return;
        }
      } else {
        if (isDbModuleActive) {
          const checkedKeys = (getValues('fields') || []).filter(f => f);

          if (Object.keys(stateChecked).length + checkedKeys.length < 1) {
            clearErrors();
            setError(errorMessage(translate('batch_update.no_field_selected', 'Bitte wählen Sie mindestens ein Feld aus.'), 'fill fields'));
            scrollToTop();
            return;
          }
          if (selected.length < 1) {
            setError(errorMessage(translate('batch_update.checked_employees_required'), 'fill fields'));
            scrollToTop();
            return;
          }
          if (checkedKeys.length > 0 && !(await trigger(checkedKeys))) return;
          const stateErr = stateRequiredError(stateChecked, stateValues);
          if (stateErr) {
            setError(errorMessage(stateErr, 'fill fields'));
            scrollToTop();
            return;
          }
        } else {
          const data = getValues();
          const checkedKeys = data.fields.filter(f => f);

          if (checkedKeys.length < 1) {
            clearErrors();
            setError(errorMessage(translate('batch_update.no_field_selected', 'Bitte wählen Sie mindestens ein Feld aus.'), 'fill fields'));
            scrollToTop();
            return;
          }
          if (selected.length < 1) {
            setError(errorMessage(translate('batch_update.checked_employees_required'), 'fill fields'));
            scrollToTop();
            return;
          }
          // Validate exclude_from_payout_reason too, so a "yes" with no reason is caught.
          const keysToValidate = checkedKeys.includes('exclude_from_payout')
            ? [...checkedKeys, 'exclude_from_payout_reason']
            : checkedKeys;
          if (!(await trigger(keysToValidate))) return;
        }
      }

    } else {
      if (instanceChecked.length < 1) {
        setError(errorMessage(translate('batch_update.checked_employees_required'), 'fill fields'));
        scrollToTop();
        return;
      }
      const { updates, error: vErr } = buildInstanceUpdates();
      if (vErr) {
        setError(vErr);
        scrollToTop();
        return;
      }
      // A shortfall means some selected person has no field ticked.
      if (updates.length < instanceChecked.length) {
        setError(errorMessage(translate('action.detail.instance.fill_required', 'Bitte füllen Sie für jede ausgewählte Person mindestens ein Feld aus.'), 'fill fields'));
        scrollToTop();
        return;
      }
    }

    setPage(2);
  };

  // Advance from the review step (page 2) to the apply step (page 3).
  // The pre-check/dry-run runs here - when first reaching page 3 - not on page 1 -> 2.
  const goToApply = async () => {
    setError(null);
    setErrorType('danger');

    // Period deactivation has no pre-check; go straight to the apply step.
    if (actionType === 'deactivate') {
      setPage(3);
      return;
    }

    // Only advance once the pre-check succeeds; on failure stay on page 2.
    if (await fetchPreview()) setPage(3);
  };

  const handleApply = async (confirmFinalAssignment = false) => {
    setError(null);
    setErrorType('danger');
    setSubmitting(true);

    try {
      if (actionType === 'deactivate') {
        const basePayload = { filters, employee_hash_ids: selected };
        const payload = confirmFinalAssignment
          ? withFinalAssignmentConfirmation(basePayload)
          : basePayload;
        const { status } = await api.post(`/employees/bulk-deactivate`, payload);
        if (status !== 200) throw new Error('unexpected status');
      } else if (actionMode === 'overwrite') {
        const { status } = await api.post('/employees/bulk-update', buildOverwriteData());
        if (status !== 200) throw new Error('unexpected status');
      } else {
        const { status } = await api.post('/employees/batch-save', { updates: buildInstanceUpdates().updates, dry_run: false });
        if (status !== 200) throw new Error('unexpected status');
      }

      toast.success(actionType === 'deactivate'
        ? translate('employee_management.bulk_deactivate_success', 'Die Beschäftigten wurden in dieser Periode deaktiviert.')
        : translate('form.success.saved'));

      navigate(backTo || '/settings/actions');
    } catch (err) {
      const confirmation = actionType === 'deactivate'
        ? finalAssignmentConfirmationDetails(err)
        : null;

      if (confirmation && !confirmFinalAssignment) {
        setFinalAssignmentCount(confirmation.count);
      } else {
        if (confirmFinalAssignment) setFinalAssignmentCount(null);
        setErrorType('danger');
        setError(errorMessage(err, 'apply fill fields action'));
        Sentry.captureException(err);
      }
      setSubmitting(false);
    }
  };

  // ---- render helpers ------------------------------------------------------

  // Auto-ticks the field so its input is editable without ticking the box first.
  const ensureFieldChecked = (key) => {
    if (!watchFields.includes(key)) setValue('fields', [...watchFields, key]);
  };

  const bindFieldInput = (key) => {
    const reg = register(key);
    return {
      ...reg,
      onChange: (e) => { reg.onChange(e); ensureFieldChecked(key); },
    };
  };

  const renderFieldInput = (field) => {
    if (field.key === 'pay_grade') {
      return <PayGradeSelect
        activePayfactorValues={activePayfactorValues}
        grade={watchPayGrade}
        level={watchPayGradeLevel}
        onGradeChange={(v) => { setValue('pay_grade', v); ensureFieldChecked('pay_grade'); }}
        onLevelChange={(v) => { setValue('pay_grade_level', v); ensureFieldChecked('pay_grade'); }}
        gradeError={errors.pay_grade?.message}
        levelError={errors.pay_grade_level?.message}
      />;
    }

    if (field.key === 'department') {
      return <DepartmentSelect
        value={watchDepartment}
        onChange={(v) => { setValue('department', v); ensureFieldChecked('department'); }}
        error={errors.department?.message}
      />;
    }

    if (field.key === 'individual_pay_factor') {
      const enabledReg = register('uses_individual_pay_factor');
      const isAuto = !watchUsesIndividualPayFactor;
      return <>
        <InputAffix
          prefix={<input type="checkbox" className="mr-2" {...enabledReg} onChange={(e) => { enabledReg.onChange(e); ensureFieldChecked('individual_pay_factor'); }} data-testid="fillfields-uses-individual-pay-factor" />}
          error={!!errors.individual_pay_factor}
          readOnly={isAuto}
        >
          <Input type="text" error={!!errors.individual_pay_factor} {...bindFieldInput('individual_pay_factor')} readOnly={isAuto} maxLength={11} />
        </InputAffix>
        {errors.individual_pay_factor && <div className="peb-field__error">{errors.individual_pay_factor.message}</div>}
      </>;
    }

    if (CURRENCY_FIELD_KEYS.includes(field.key)) {
      return <>
        <InputAffix prefix="€" error={!!errors[field.key]}>
          <Input type="text" error={!!errors[field.key]} {...bindFieldInput(field.key)} maxLength={24} />
        </InputAffix>
        {errors[field.key] && <div className="peb-field__error">{errors[field.key].message}</div>}
      </>;
    }

    if (field.key === 'missing_months' || DAYS_SUFFIX_FIELD_KEYS.includes(field.key)) {
      const suffix = field.key === 'missing_months'
        ? editableTranslate('form.months', 'Monate')
        : editableTranslate('form.days', 'Tage');
      return <>
        <InputAffix suffix={suffix} error={!!errors[field.key]}>
          <Input type="text" error={!!errors[field.key]} {...bindFieldInput(field.key)} maxLength={7} />
        </InputAffix>
        {errors[field.key] && <div className="peb-field__error">{errors[field.key].message}</div>}
      </>;
    }

    if (field.available_values) {
      return <>
        <NativeSelect error={!!errors[field.key]} {...bindFieldInput(field.key)}>
          <option value="">{translate('form.select_placeholder')}</option>
          {Object.entries(field.available_values).map(([value, translationKey]) => (
            <option key={value} value={value}>{translate(translationKey)}</option>
          ))}
        </NativeSelect>
        {errors[field.key] && <div className="peb-field__error">{errors[field.key].message}</div>}
      </>;
    }

    if (field.key === 'exclude_from_payout') {
      // The reason input is only editable when "yes"; editing either ticks the checkbox.
      const excludeReg = register('exclude_from_payout');
      const reasonReg = register('exclude_from_payout_reason');
      const excluded = watchExcludeFromPayout === 'true' || watchExcludeFromPayout === true;
      return <>
        <Field error={errors.exclude_from_payout_reason?.message}>
          <div className="d-flex select-with-input">
            <NativeSelect className="w-auto" {...excludeReg} onChange={(e) => { excludeReg.onChange(e); ensureFieldChecked('exclude_from_payout'); }}>
              <option value="false">{translate('form.no', 'nein')}</option>
              <option value="true">{translate('form.yes', 'Ja')}</option>
            </NativeSelect>
            <Input type="text" error={!!errors.exclude_from_payout_reason} {...reasonReg} onChange={(e) => { reasonReg.onChange(e); ensureFieldChecked('exclude_from_payout'); }} readOnly={!excluded} maxLength={50} />
          </div>
        </Field>
      </>;
    }

    if (field.key === 'working_hours') {
      return <>
        <InputAffix
          error={!!errors[field.key]}
          suffix={softwareSettings?.working_hours_type === "hours"
            ? editableTranslate("general_settings.working_hours_type.hours")
            : editableTranslate("general_settings.working_hours_type.percentage")}
        >
          <Input type="text" error={!!errors[field.key]} {...bindFieldInput(field.key)} maxLength={24} />
        </InputAffix>
        {errors[field.key] && <div className="peb-field__error">{errors[field.key].message}</div>}
      </>;
    }

    if (field.key === 'employee_group') {
      const groupOptions = (groups || []).map(g => ({ value: g.hash_id, label: g.name }));
      return <>
        <div style={{ display: 'contents' }} data-testid="fill-value-select">
          <Select
            error={!!errors[field.key]}
            options={groupOptions}
            value={groupOptions.find(o => o.value === watchEmployeeGroup) || null}
            onChange={(option) => { setValue('employee_group', option ? option.value : '', { shouldValidate: true }); ensureFieldChecked('employee_group'); }}
            isClearable
            placeholder={translate('form.select_placeholder')}
          />
        </div>
        {errors[field.key] && <div className="peb-field__error">{errors[field.key].message}</div>}
      </>;
    }

    const isDate = field.key.includes('date');
    return <Field error={errors[field.key]?.message}>
      {isDate
        // bindFieldInput ticks the field's checkbox on every change, which is
        // what the hand-wired picker used to do for a calendar pick.
        ? <DateInput error={!!errors[field.key]} {...bindFieldInput(field.key)} maxLength={255} />
        : <Input type="text" error={!!errors[field.key]} autoComplete="on" {...bindFieldInput(field.key)} maxLength={255} />}
    </Field>;
  };

  const renderFieldCheckbox = (field, i, onChange = undefined, colClass = 'col-md-6 col-xl2-4') => {
    let translatedName = fieldCustomNames?.[field?.translation_key] || editableTranslate(`field.${field.key.replace('_hash_id', '')}`, field.label);

    if (field.key === 'exclude_from_payout') {
      translatedName = fieldCustomNames?.['field.exclude_from_payout'] || editableTranslate(`field.exclude_from_payout`, 'Ausschluss (Grenzwert)');
    } else if (isDbModuleActive && field.key === 'balanced_scorecard_template_group') {
      translatedName = fieldCustomNames?.['field.balanced_scorecard_template'] || editableTranslate('state_employee.rating_mode');
    } else if (isDbModuleActive && field.key === 'balanced_scorecard_template') {
      translatedName = translate('state_employee.rating_mode.template');
    }

    // Keep RHF's own onChange unless a custom handler is supplied, or the input stays disabled.
    const fieldsReg = register('fields');

    return <div key={i} className={colClass}>
      <div className="form-group">
        <div className="form-check mb-1">
          <input
            type="checkbox"
            id={`fillfields-checkbox-${field.key}`}
            data-testid={`fillfields-checkbox-${field.key}`}
            className="form-check-input"
            value={field.key}
            {...fieldsReg}
            onChange={(e) => { if (onChange) { onChange(e); } else { fieldsReg.onChange(e); } }}
          />
          <label className="form-check-label ml-1" htmlFor={`fillfields-checkbox-${field.key}`}>{translatedName}</label>
        </div>
        {renderFieldInput(field)}
      </div>
    </div>;
  };

  const renderSkippedWarnings = (skipped) => {
    if (!skipped) return null;
    const blocks = [];
    for (const k in skipped) {
      if (skipped[k]?.length > 0) {
        let translationKey = k;
        if (!['employment_date_invalid', 'previous_evaluation_locked'].includes(k)) {
          translationKey += '_lk_error';
        }
        blocks.push(
          <div key={k} className="mb-2">
            <div>{editableTranslate(`batch_update.${isDbModuleActive ? 'state_' : ''}${k}`)}</div>
            {skipped[k].map((e, i) => <div key={i}>- <a href={employeeManagementPath({ staffId: e.employee_number })} target="_blank" rel="noopener noreferrer" data-testid={`employee-link-${e.employee_number}`}>{e.full_name || e.employee_number}</a></div>)}
          </div>
        );
      }
    }
    if (!blocks.length) return null;

    return (
      <div className="alert alert-warning mt-3">
        <div className="mb-2">{blocks}</div>
      </div>
    );
  };

  // Only the first load blanks the form, so the search box keeps focus during later fetches.
  const loading = !fixedFields || !poolLoaded;

  const poolPaginationProps = {
    search: searchInput,
    onSearchChange: setSearchInput,
    total: pagination.total,
    loadingPool,
    page: pagination.currentPage,
    perPage: pagination.perPage,
    totalPages: pagination.totalPages,
    totalRows: pagination.total,
    setPage: setPoolPage,
    setPerPage: handlePoolPerPageChange,
  };

  const [summaryTextKey, summaryTextAlt] = useMemo(() => {
    if (actionType === 'update_data') {
      return ['action.detail.summary_text', '$X Beschäftigte werden betroffen sein.'];
    } else if (actionType === 'deactivate') {
      return ['action.detail.summary_deactivate', '$X Beschäftigte werden in dieser Periode deaktiviert.'];
    } else if (actionType === 'create_balanced_scorecard') {
      if (isDbModuleActive) {
        return ['action.detail.summary_evaluation', 'Für $X Beschäftigte wird eine Beurteilung erstellt.'];
      } else {
        return ['action.detail.summary_balanced_scorecard', 'Für $X Beschäftigte wird eine Leistungskarte erstellt.'];
      }
    }
    // Sub-page tabs (change_first_scorecard) render their own summary.
    return [];
  }, [actionType, isDbModuleActive]);

  // The three action-type tabs shared with the embedded create-balanced-scorecard flow,
  // so its tab bar can switch back to the sibling action types.
  const scorecardTabItems = useMemo(() => {
    const items = [
      { key: 'update_data', icon: 'bi-person', label: editableTranslate('action.detail.update_data', 'Daten aktualisieren') },
      { key: 'deactivate', icon: 'bi-person-x', label: editableTranslate('action.detail.deactivate_employees', 'In dieser Periode deaktivieren') },
      {
        key: 'create_balanced_scorecard',
        icon: 'bi-person-square',
        label: isDbModuleActive
          ? editableTranslate('action.detail.create_evaluation', 'Beurteilung erstellen')
          : editableTranslate('action.detail.create_balanced_scorecard', 'Leistungskarte anlegen'),
      },
      {
        key: 'change_first_scorecard',
        icon: 'bi-arrow-repeat',
        label: isDbModuleActive
          ? editableTranslate('action.detail.change_first_evaluation', 'Erste Beurteilung ändern')
          : editableTranslate('action.detail.change_first_scorecard', 'Erste Leistungskarte ändern'),
      },
    ];
    // Validation-driven actions only ever update data.
    if (restrictToUpdateData) return items.filter(t => t.key === 'update_data');

    return items.filter(t => (t.key !== 'create_balanced_scorecard' || canCreateScorecards)
      && (t.key !== 'change_first_scorecard' || canChangeFirstScorecard));
  }, [isDbModuleActive, editableTranslate, restrictToUpdateData, canCreateScorecards, canChangeFirstScorecard]);

  const handleTabChange = (key) => {
    if (restrictToUpdateData && key !== 'update_data') return;
    if (key === 'deactivate') changeActionMode('overwrite');
    setActionType(key);
  };

  return (
    <div className="mt-3">
      <hr className="mt-2 mb-4" />

      {
        error &&
        <div className="mb-3">
          <ErrorAlert error={error} onClose={() => { setError(null); setErrorType('danger'); }} isHtml={true} type={errorType} />
        </div>
      }

      {
        actionType === 'create_balanced_scorecard'
          ? <CreateBalancedScorecardAction
            filters={filters}
            page={page}
            setPage={setPage}
            setFilterCount={setFilterCount}
            tabs={scorecardTabItems}
            activeTab={actionType}
            onTabChange={handleTabChange}
            onBack={onBack}
            asSubPage={true}
            sharedSelection={{ actionMode, setActionMode: changeActionMode, selected, setSelected, selectedEmployees, setSelectedEmployees }}
            sharedPool={{ employees, loadingPool, poolLoaded, poolPaginationProps }}
          />
          : actionType === 'change_first_scorecard'
          ? <ChangeFirstScorecardAction
            filters={filters}
            page={page}
            setPage={setPage}
            setFilterCount={setFilterCount}
            tabs={scorecardTabItems}
            activeTab={actionType}
            onTabChange={handleTabChange}
            onBack={onBack}
            sharedSelection={{ actionMode, setActionMode: changeActionMode, selected, setSelected, selectedEmployees, setSelectedEmployees }}
            sharedPool={{ employees, loadingPool, poolLoaded, poolPaginationProps }}
          />
          : loading
            ? <div className="text-center p-4" data-testid="loading">
              <div className="spinner-border" role="status"><span className="sr-only">Wird geladen...</span></div>
            </div>
            : <>
              {
                filterCount === 0 ? <div>
                  <div className="alert alert-warning">
                    <span>{editableTranslate('action.detail.no_employees_found', 'Keine Beschäftigten gefunden, die diesen Kriterien entsprechen.')}</span>
                  </div>

                  <div className="settings-sticky-footer">
                    {onBack
                      ? <Button variant="secondary" onClick={onBack} data-testid="back-button">
                        {editableTranslate('button.back', 'Zurück')}
                      </Button>
                      : <Button as={Link} data-testid="back-button" variant="secondary" to={backTo || `/settings/actions`}>
                        {editableTranslate('button.back', 'Zurück')}
                      </Button>}
                  </div>
                </div> : <>
                  {
                    page === 1 && <>
                      {
                        actionMode === 'overwrite' && <div className="mb-5">
                          <div className="font-weight-bold">{editableTranslate('action.detail.employees', 'Beschäftigte')}</div>
                          <hr className="my-2" />
                          <div className="mb-3">{editableTranslate('action.detail.select_employees', 'Wählen Sie die Beschäftigten aus, auf die die Änderungen angewendet werden sollen.')}</div>
                          <EmployeeSelector employees={employees || []} selected={selected} setSelected={setSelected} onSelectionChange={(emps, checked) => setSelectedEmployees(prev => applySelectionChange(prev, emps, checked))} {...poolPaginationProps} />
                        </div>
                      }

                      <div className="mb-3">
                        <div className="action-tab-mode-div">
                          <div className="flex-1 border-bottom">
                            <div className="employee-management-tabs">
                              <div className={`cursor-pointer ${actionType === 'update_data' ? 'active' : ''}`} onClick={() => setActionType('update_data')} data-testid="update-data-tab">
                                <i className="bi bi-person mr-1"></i>
                                <span>{editableTranslate('action.detail.update_data', 'Daten aktualisieren')}</span>
                              </div>
                              {!restrictToUpdateData && <div className={`cursor-pointer ${actionType === 'deactivate' ? 'active' : ''}`} onClick={() => { setActionType('deactivate'); changeActionMode('overwrite'); }} data-testid="deactivate-tab">
                                <i className="bi bi-person-x mr-1"></i>
                                <span>{editableTranslate('action.detail.deactivate_employees', 'In dieser Periode deaktivieren')}</span>
                              </div>}
                              {!restrictToUpdateData && canCreateScorecards && <div className={`cursor-pointer ${actionType === 'create_balanced_scorecard' ? 'active' : ''}`} onClick={() => setActionType('create_balanced_scorecard')} data-testid="create-balanced-scorecard-tab">
                                <i className="bi bi-person-square mr-1"></i>
                                {
                                  isDbModuleActive ? <span>{editableTranslate('action.detail.create_evaluation', 'Beurteilung erstellen')}</span> :
                                    <span>{editableTranslate('action.detail.create_balanced_scorecard', 'Leistungskarte anlegen')}</span>
                                }
                              </div>}
                              {!restrictToUpdateData && canChangeFirstScorecard && <div className={`cursor-pointer ${actionType === 'change_first_scorecard' ? 'active' : ''}`} onClick={() => setActionType('change_first_scorecard')} data-testid="change-first-scorecard-tab">
                                <i className="bi bi-arrow-repeat mr-1"></i>
                                {
                                  isDbModuleActive ? <span>{editableTranslate('action.detail.change_first_evaluation', 'Erste Beurteilung ändern')}</span> :
                                    <span>{editableTranslate('action.detail.change_first_scorecard', 'Erste Leistungskarte ändern')}</span>
                                }
                              </div>}
                            </div>
                          </div>
                          <div className="btn-group btn-group-switch-mode normal-border padding-normal" role="group">
                            <button type="button" className={`btn ${actionMode === 'overwrite' ? ' active' : ''}`} onClick={() => changeActionMode('overwrite')} data-testid="mode-overwrite-button">
                              {editableTranslate('action.detail.mode.overwrite', 'Überschreiben')}
                            </button>
                            <button type="button" className={`btn ${actionMode === 'per_instance' ? ' active' : ''}`} onClick={() => { actionType !== 'deactivate' && changeActionMode('per_instance'); }} disabled={actionType === 'deactivate'} data-tooltip-id={actionType === 'deactivate' ? `fill-per-instance-deactivate-tooltip` : ''} data-testid="mode-per-instance-button">
                              {editableTranslate('action.detail.mode.per_instance', 'Pro Beschäftigte ausfüllen')}
                            </button>
                            <ReactTooltip id={`fill-per-instance-deactivate-tooltip`} content={editableTranslate('action.detail.fill_per_instance_deactivate_tooltip', 'Die Periodenaktivität kann nur gemeinsam geändert werden.')} place="bottom" clickable={true} />
                          </div>
                        </div>
                      </div>

                      {
                        actionMode === 'overwrite' ? <>
                          {
                            actionType === 'update_data' && <div>
                              <div className="mb-3">{editableTranslate('action.detail.fill.intro', 'Wählen Sie die Felder aus, die Sie für die ausgewählten Beschäftigten ausfüllen möchten.')}</div>

                              <div>
                                {
                                  isDbModuleActive
                                    ? <div className="action-update-group">
                                      <div className="font-weight-bold">{editableTranslate('batch_update.personal_data', 'Personaldaten')}</div>
                                      <hr className="mt-2" />
                                      <StateOverwriteForm
                                        sections={stateSections}
                                        checked={stateChecked}
                                        setChecked={setStateChecked}
                                        values={stateValues}
                                        setValues={setStateValues}
                                        prepend={stateExtraFields.length > 0 ? stateExtraFields.map((field, i) => renderFieldCheckbox(field, i, undefined, 'col-xl-4 col-lg-6')) : null}
                                      />
                                    </div>
                                    : <>
                                      {
                                        personaldatenFields.length > 0 && <div className="action-update-group">
                                          <div className="font-weight-bold">{editableTranslate('batch_update.personal_data', 'Personaldaten')}</div>
                                          <hr className="mt-2" />
                                          <div className="row">
                                            {personaldatenFields.map((field, i) => renderFieldCheckbox(field, i))}
                                          </div>
                                        </div>
                                      }

                                      {
                                        abrechnungsdatenFields.length > 0 && <div className="action-update-group">
                                          <div className="font-weight-bold">{editableTranslate('batch_update.billing_data', 'Abrechnungsdaten')}</div>
                                          <hr className="mt-2" />
                                          <div className="row">
                                            {abrechnungsdatenFields.map((field, i) => renderFieldCheckbox(field, i))}
                                          </div>
                                        </div>
                                      }

                                      {
                                        freeFields.length > 0 && <div className="action-update-group">
                                          <div className="font-weight-bold">{editableTranslate('global.menu.settings.free_fields', 'Freifelder')}</div>
                                          <hr className="mt-2" />
                                          <div className="row">
                                            {freeFields.map((field, i) => renderFieldCheckbox(field, i))}
                                          </div>
                                        </div>
                                      }
                                    </>
                                }
                              </div>
                            </div>
                          }
                          {
                            actionType === 'deactivate' && <div>
                              <div className="mb-3">{editableTranslate('action.detail.deactivate_employees.intro', 'Die ausgewählten Beschäftigten werden für die aktuelle Periode deaktiviert.')}</div>
                            </div>
                          }
                        </> : <div className="">
                          <EmployeeInstanceEditor
                            employees={employees || []}
                            {...poolPaginationProps}
                            personaldatenFields={personaldatenFields}
                            abrechnungsdatenFields={abrechnungsdatenFields}
                            freeFields={freeFields}
                            groups={groups}
                            checked={instanceChecked}
                            setChecked={setInstanceChecked}
                            data={instanceData}
                            setData={setInstanceData}
                            fieldChecked={instanceFieldChecked}
                            setFieldChecked={setInstanceFieldChecked}
                            isDbModuleActive={isDbModuleActive}
                            stateExtraFields={stateExtraFields}
                            stateSections={stateSections}
                            stateChecked={stateInstanceChecked}
                            setStateChecked={setStateInstanceChecked}
                            stateValues={stateInstanceValues}
                            setStateValues={setStateInstanceValues}
                            softwareSettings={softwareSettings}
                            fieldCustomNames={fieldCustomNames}
                            activePayfactorValues={activePayfactorValues}
                            onSelectionChange={(emps, checked) => setCheckedEmployees(prev => applySelectionChange(prev, emps, checked))}
                          />
                        </div>
                      }

                      <div className="settings-sticky-footer mt-3">
                        {onBack
                          ? <Button variant="secondary" onClick={onBack} data-testid="back-button" className="mr-2">
                            {editableTranslate('button.back', 'Zurück')}
                          </Button>
                          : <Button as={Link} data-testid="back-button" variant="secondary" className="mr-2" to={backTo || `/settings/actions`}>
                            {editableTranslate('button.back', 'Zurück')}
                          </Button>}
                        <Button variant="primary" onClick={goToPrecheck} disabled={(actionMode === 'overwrite' && selected.length === 0) || (actionMode === 'per_instance' && instanceChecked.length === 0)} data-testid="next-button">
                          {editableTranslate('button.next', 'Weiter')}
                        </Button>
                      </div>
                    </>
                  }

                  {
                    page === 2 && <>
                      <div>
                        {
                          actionType === 'deactivate'
                            ? <ActionChangePreview whatItems={[{ label: editableTranslate('action.detail.deactivate_employees', 'In dieser Periode deaktivieren') }]} whoList={buildWhoList(selected, selectedEmployees)} />
                            : actionMode === 'overwrite'
                              ? <ActionChangePreview whatItems={getChangedValues()} whoList={buildWhoList(selected, selectedEmployees)} />
                              : <ActionChangePreview perInstance whoList={pairInstanceValues(instanceChecked, checkedEmployees, getInstanceChangedValues)} />
                        }
                      </div>

                      <div className="settings-sticky-footer d-flex mt-3">
                        <Button variant="secondary" onClick={() => setPage(1)} data-testid="back-button" className="mr-2">
                          {editableTranslate('button.back', 'Zurück')}
                        </Button>
                        <Button variant="primary" onClick={goToApply} loading={loadingPreview} data-testid="proceed-button">{editableTranslate('button.proceed', 'Fortfahren')}</Button>
                      </div>
                    </>
                  }

                  {
                    page === 3 && <>
                      <div>
                        <div className="font-weight-bold">{editableTranslate('action.detail.step.summary', 'Zusammenfassung')}</div>
                        <hr className="mt-2" />
                        {
                          actionMode === 'overwrite'
                            ? <p className="mb-0" dangerouslySetInnerHTML={{
                              __html: editableTranslateString(summaryTextKey, summaryTextAlt).replace('$X', preview?.affected_count ?? selected.length)
                            }} />
                            : <p className="mb-0" dangerouslySetInnerHTML={{
                              __html: editableTranslateString(summaryTextKey, summaryTextAlt).replace('$X', preview?.affected_count ?? instanceChecked.length)
                            }} />
                        }
                        {preview && renderSkippedWarnings(preview.skipped_employees)}
                      </div>

                      <div className="settings-sticky-footer d-flex mt-3">
                        <Button variant="secondary" onClick={() => setPage(2)} disabled={submitting} data-testid="back-button" className="mr-2">
                          {editableTranslate('button.back', 'Zurück')}
                        </Button>
                        <Button variant="primary" onClick={() => handleApply(false)} loading={submitting || loadingPreview} disabled={(actionType !== 'deactivate' && (preview?.affected_count ?? 0) === 0)} data-testid="apply-button">{editableTranslate('button.apply', 'Anwenden')}</Button>
                      </div>
                    </>
                  }
                </>
              }
            </>
      }
      <EmployeeDeactivationModal
        show={finalAssignmentCount !== null}
        loading={submitting}
        employeeCount={selected.length}
        finalAssignmentCount={finalAssignmentCount}
        requiresFinalAssignmentConfirmation={true}
        onCancel={() => setFinalAssignmentCount(null)}
        onConfirm={() => handleApply(true)}
        testId="aktion-final-assignment-confirmation"
      />
    </div>
  );
}
